import { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';

const API_URL = 'https://songmoment.onrender.com';

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function StatCard({ label, value, emoji }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '0.5px solid rgba(212,83,126,0.2)',
      borderRadius: 14, padding: '16px', flex: 1, minWidth: 0,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{emoji}</div>
      <div style={{ fontSize: 24, fontWeight: 300, fontFamily: "'Cormorant Garamond', serif", color: '#D4537E' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

export default function ProfileModal({ onClose }) {
  const { user, token } = useApp();
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
  if (!user?.id) return;
  fetchMyMoments();
}, [user?.id]);

  const fetchMyMoments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/moments/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMoments(res.data);
    } catch (err) {
      try {
        const res = await axios.get(`${API_URL}/api/moments`);
        setMoments(res.data.filter(m => m.user_id === user.id || m.username === user.username));
      } catch (e) { console.error(e); }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (momentId) => {
    if (!window.confirm('Delete this moment?')) return;
    setDeleting(momentId);
    try {
      await axios.delete(`${API_URL}/api/moments/${momentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMoments(prev => prev.filter(m => m.id !== momentId));
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete moment');
    } finally {
      setDeleting(null);
    }
  };

  const totalMoments   = moments.length;
  const totalReactions = moments.reduce((sum, m) => sum + (m.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0), 0);
  const totalComments  = moments.reduce((sum, m) => sum + (m.comment_count || 0), 0);
  const mostReacted    = moments.reduce((best, m) => {
    const count     = (m.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0);
    const bestCount = (best?.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0);
    return count > bestCount ? m : best;
  }, null);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(8,6,8,0.88)', backdropFilter: 'blur(16px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '20px', overflowY: 'auto',
    }} onClick={e => e.target === e.currentTarget && onClose()}>

      <div className="fade-in" style={{
        background: 'rgba(255,255,255,0.02)',
        border: '0.5px solid rgba(212,83,126,0.25)',
        borderRadius: 20, width: '100%', maxWidth: 620,
        marginTop: 20, marginBottom: 40,
      }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'linear-gradient(135deg, #993556, #D4537E)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 500, color: '#fff',
            }}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 22, color: '#f5e6ed', letterSpacing: '0.02em' }}>
                @{user?.username}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(212,83,126,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 2 }}>
                Your profile
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'rgba(245,230,237,0.45)', background: 'rgba(255,255,255,0.04)',
            border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 20,
            padding: '6px 16px', cursor: 'pointer',
          }}>✕ Close</button>
        </div>

        {/* Stats */}
        <div style={{ padding: '20px 24px 0', display: 'flex', gap: 10 }}>
          <StatCard label="Moments"   value={totalMoments}   emoji="🎵" />
          <StatCard label="Reactions" value={totalReactions} emoji="🔥" />
          <StatCard label="Comments"  value={totalComments}  emoji="💬" />
        </div>

        {/* Most reacted */}
        {mostReacted && totalReactions > 0 && (
          <div style={{ margin: '16px 24px 0', padding: '14px 16px', background: 'rgba(212,83,126,0.06)', borderRadius: 14, border: '0.5px solid rgba(212,83,126,0.2)' }}>
            <p style={{ fontSize: 10, color: 'rgba(212,83,126,0.8)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
              🏆 Most Reacted Moment
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {mostReacted.album_image
                ? <img src={mostReacted.album_image} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(212,83,126,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>♪</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: '#f5e6ed' }}>{mostReacted.track_name}</div>
                <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.45)' }}>{mostReacted.artist_name} · {formatTime(mostReacted.timestamp_ms)}</div>
              </div>
              <div style={{ fontSize: 11, color: '#D4537E', letterSpacing: '0.06em', flexShrink: 0 }}>
                {(mostReacted.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0)} reactions
              </div>
            </div>
          </div>
        )}

        {/* Divider */}
        <div style={{ margin: '20px 24px 0', height: '0.5px', background: 'linear-gradient(90deg, transparent, rgba(212,83,126,0.25), transparent)' }} />

        {/* Moments list */}
        <div style={{ padding: '16px 24px 28px' }}>
          <p style={{ fontSize: 10, color: 'rgba(212,83,126,0.7)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
            Your Moments ({totalMoments})
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(245,230,237,0.35)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Loading your moments...
            </div>
          ) : moments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>♪</div>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 300, color: 'rgba(245,230,237,0.5)', marginBottom: 6 }}>No moments yet</p>
              <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Share your first one!</p>
            </div>
          ) : (
            moments.map(m => {
              const reactionCount = (m.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0);
              return (
                <div key={m.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '0.5px solid rgba(212,83,126,0.12)',
                  borderRadius: 14, padding: '12px 14px', marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 0.2s',
                }}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.3)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.12)'}
                >
                  {m.album_image
                    ? <img src={m.album_image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 44, height: 44, borderRadius: 8, background: 'rgba(212,83,126,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>♪</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: '#f5e6ed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.track_name}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.45)', marginTop: 2 }}>{m.artist_name}</div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 5, fontSize: 10, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.04em' }}>
                      <span>⏱ {formatTime(m.timestamp_ms)}</span>
                      <span>🔥 {reactionCount}</span>
                      <span>💬 {m.comment_count || 0}</span>
                      <span>{timeAgo(m.created_at)}</span>
                    </div>
                    {m.note && (
                      <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)', marginTop: 5, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        "{m.note}"
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(m.id)}
                    disabled={deleting === m.id}
                    style={{
                      flexShrink: 0,
                      background: 'rgba(248,113,113,0.06)',
                      border: '0.5px solid rgba(248,113,113,0.25)',
                      borderRadius: 20, padding: '6px 12px',
                      fontSize: 13, cursor: 'pointer',
                      color: 'rgba(248,113,113,0.7)',
                      opacity: deleting === m.id ? 0.4 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(248,113,113,0.15)'}
                    onMouseOut={e => e.currentTarget.style.background = 'rgba(248,113,113,0.06)'}
                  >
                    {deleting === m.id ? '⏳' : '🗑️'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}